import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox,
  TrendingUp,
  CheckCircle2,
  CalendarClock,
  LogOut,
  Trash2,
  Mail,
  Phone,
  Building2,
  Search,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/site/Logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUS = {
  new: { label: "New", cls: "bg-[#98D8BA] text-[#0B281B]" },
  contacted: { label: "Contacted", cls: "bg-[#F0F5F2] text-[#4A6657] border border-[#DCECE3]" },
  closed: { label: "Closed", cls: "bg-[#0B281B] text-white" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, this_week: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      setLeads((prev) => prev.filter((l) => l.id !== id));
      load();
    } catch (err) {
      toast.error("Could not delete lead");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const filtered = leads.filter((l) => {
    const matchFilter = filter === "all" || l.status === filter;
    const q = query.toLowerCase();
    const matchQuery =
      !q ||
      l.name?.toLowerCase().includes(q) ||
      l.email?.toLowerCase().includes(q) ||
      l.business_name?.toLowerCase().includes(q);
    return matchFilter && matchQuery;
  });

  const cards = [
    { label: "Total leads", value: stats.total, icon: Inbox },
    { label: "New", value: stats.new, icon: TrendingUp },
    { label: "This week", value: stats.this_week, icon: CalendarClock },
    { label: "Contacted", value: stats.contacted, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-[#FAFCFA]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[#DCECE3] bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#4A6657] sm:inline" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#DCECE3] px-4 py-2 text-sm font-medium text-[#0B281B] transition-colors hover:bg-[#F0F5F2]"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <h1 className="font-heading text-3xl font-medium text-[#0B281B]">Leads</h1>
        <p className="mt-1 text-[#4A6657]">Manage enquiries from your website.</p>

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.label}
                data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-xl border border-[#DCECE3] bg-white p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#4A6657]">{c.label}</span>
                  <Icon className="h-5 w-5 text-[#75C69F]" />
                </div>
                <p className="mt-3 font-heading text-3xl font-bold text-[#0B281B]">
                  {c.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A6657]" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, business…"
              className="w-full rounded-xl border border-[#DCECE3] bg-white py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#98D8BA]"
            />
          </div>
          <div className="flex gap-2" data-testid="admin-filters">
            {["all", "new", "contacted", "closed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
                  filter === f
                    ? "bg-[#0B281B] text-white"
                    : "border border-[#DCECE3] text-[#4A6657] hover:bg-[#F0F5F2]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="mt-6 space-y-4">
          {loading ? (
            <p className="py-16 text-center text-[#4A6657]">Loading leads…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#DCECE3] bg-white py-20 text-center" data-testid="admin-empty">
              <Inbox className="mx-auto h-10 w-10 text-[#98D8BA]" />
              <p className="mt-3 font-medium text-[#0B281B]">No leads found</p>
              <p className="text-sm text-[#4A6657]">
                New enquiries from your contact form will appear here.
              </p>
            </div>
          ) : (
            filtered.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className="rounded-2xl border border-[#DCECE3] bg-white p-6"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading text-lg font-medium text-[#0B281B]">
                        {l.name}
                      </h3>
                      <span
                        className={`rounded-full px-3 py-0.5 text-xs font-bold uppercase tracking-wider ${STATUS[l.status]?.cls || ""}`}
                      >
                        {STATUS[l.status]?.label || l.status}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4A6657]">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-[#0B281B]">
                        <Mail className="h-4 w-4" /> {l.email}
                      </a>
                      {l.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-4 w-4" /> {l.phone}
                        </span>
                      )}
                      {l.business_name && (
                        <span className="flex items-center gap-1.5">
                          <Building2 className="h-4 w-4" /> {l.business_name}
                        </span>
                      )}
                    </div>

                    {(l.service_interest || l.budget) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {l.service_interest && (
                          <span className="rounded-full bg-[#F0F5F2] px-3 py-1 text-xs text-[#0B281B]">
                            {l.service_interest}
                          </span>
                        )}
                        {l.budget && (
                          <span className="rounded-full bg-[#F0F5F2] px-3 py-1 text-xs text-[#0B281B]">
                            Budget: {l.budget}
                          </span>
                        )}
                      </div>
                    )}

                    {l.message && (
                      <p className="mt-3 rounded-xl bg-[#FAFCFA] p-3 text-sm text-[#4A6657]">
                        {l.message}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-[#4A6657]/70">
                      {new Date(l.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger
                        className="w-36 rounded-xl border-[#DCECE3] text-sm"
                        data-testid={`lead-status-${l.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      onClick={() => remove(l.id)}
                      data-testid={`lead-delete-${l.id}`}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#DCECE3] text-[#E27B7B] transition-colors hover:bg-[#E27B7B] hover:text-white"
                      aria-label="Delete lead"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
